import React, { useEffect, useState, useContext } from 'react'
import { createColumnHelper } from '@tanstack/react-table'
import {
  ReloadIcon,
  MagnifyingGlassIcon,
  Cross2Icon,
  MixerHorizontalIcon,
  DownloadIcon,
} from '@radix-ui/react-icons'
import { useSearchParams } from 'react-router-dom'

import useTitle from '../../../hooks/useTitle'
import { Button } from '../../../components/ui/button'
import axiosInstance from '../../../api/axiosInstance'
import { AuthContext } from '../../../contexts/auth-context'
import GenericTable from '../../../components/generic-data-table/GenericTable'
import DataTableShimmer from '../../../components/data-table/DataTableShimmer'
import { toast } from 'sonner'
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

const columnHelper = createColumnHelper()

const INITIAL_FILTERS = {
  searchKeyword: '',
  status: '',
  gender: '',
  startDate: '',
  endDate: '',
}

function BusinessReportsCustomerDemographics() {
  const { auth } = useContext(AuthContext)
  const businessId = auth.user._id
  useTitle('Customers Demographics')

  const [searchParams, setSearchParams] = useSearchParams()

  const getFiltersFromURL = () => ({
    searchKeyword: searchParams.get('searchKeyword') || '',
    status: searchParams.get('status') || '',
    gender: searchParams.get('gender') || '',
    startDate: searchParams.get('startDate') || '',
    endDate: searchParams.get('endDate') || '',
  })

  const [filterInputs, setFilterInputs] = useState(getFiltersFromURL)
  const [activeFilters, setActiveFilters] = useState(getFiltersFromURL)
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    totalCustomers: 0,
    totalPages: 1,
  })
  const [selectedRow, setSelectedRow] = useState(null)
  const [isExporting, setIsExporting] = useState(false)
  const [businessForm, setBusinessForm] = useState([])
  const [registerForm, setRegisterForm] = useState([])
  const [formId, setFormId] = useState("")
  const [isLoadingForms, setIsLoadingForms] = useState(false)

  const getFormList = async () => {
    try {
      const payload = {
        businessId: businessId,
      }
      const response = await axiosInstance.post('/businessFormList', payload, {
        headers: {
          'Content-Type': 'application/json',
        },
      })
      if (response.status === 200) {
        const forms = response?.data?.data || [];
        setBusinessForm(forms);
        const activeForm = forms.find(form => form.isActive === true);

        if (activeForm) {
          setFormId(activeForm._id);
        } else {
          console.warn("No active form found");
        }
      }
    } catch (error) {
      console.error('Error fetching profile:', error)
    }
  }

  const getregisterFormList = async (page = 1, limit = 10, searchKeyword = '', startDate = '',endDate = '') => {
    try {
      setIsLoadingForms(true);
      const payload = {
        businessId: businessId,
        page: page,
        limit: limit,
        searchKeyword: searchKeyword // This sends search to backend
      };
      if (formId) {  // only add formId if a specific form is selected
        payload.formId = formId;
      }
      if (startDate) {  
        payload.startDate = startDate;
      }if (endDate) {  
        payload.endDate = endDate;
      }
      const response = await axiosInstance.post('/getBusinessRegisterForm', payload, {
        headers: {
          'Content-Type': 'application/json',
        },
      })
      if (response.status === 200) {
        setRegisterForm(response?.data?.data || []);

        // Update pagination from API response
        if (response.data.pagination) {
          setPagination(prev => ({
            ...prev,
            totalCustomers: response.data.pagination.totalBusiness,
            totalPages: response.data.pagination.totalPages,
            currentPage: response.data.pagination.currentPage,
            page: response.data.pagination.currentPage,
            limit: response.data.pagination.limit
          }));
        }
      }
    } catch (error) {
      console.error('Error fetching register form:', error)
      toast.error('Failed to load customer data')
    } finally {
      setIsLoadingForms(false);
    }
  }

  useEffect(() => {
    getFormList()
  }, [])

  const handleClick = (item) => {
    setFormId(item._id)
  };

  // This useEffect handles form changes and search filter changes
  useEffect(() => {
    // Reset to page 1 when form changes or search filter changes
    setPagination(prev => ({ ...prev, page: 1 }));

    getregisterFormList(1, pagination.limit, activeFilters.searchKeyword,activeFilters.startDate,activeFilters.endDate);
  }, [formId, activeFilters.searchKeyword,activeFilters.startDate, activeFilters.endDate])

  // This useEffect handles pagination changes (page/limit)
  useEffect(() => {
    if (formId || activeFilters.startDate || activeFilters.endDate || activeFilters.searchKeyword) {
      getregisterFormList(pagination.page, pagination.limit, activeFilters.searchKeyword,activeFilters.startDate,activeFilters.endDate);
    }
  }, [pagination.page, pagination.limit])

  const transformedData = registerForm?.map(item => {
    const { name, email, mobile_number, createdAt, other_data, ...rest } = item;

    // const cleanNumber = item.mobile_number.replace(/^AE\s*\+\d+\s*/, '');
    const cleanNumber = item.mobile_number.replace(/^[A-Z]{2}\s*/, '');

    const excludedKeys = [
      "businessId",
      "card_allocation_status",
      "formId",
      "updatedAt",
      "__v",
      "_id"
    ];

    let normalizedOtherData = {};
    if (other_data && typeof other_data === "object") {
      normalizedOtherData = other_data.other_data ? other_data.other_data : other_data;
    }

    const restOtherData = Object.keys(rest).reduce((acc, key) => {
      if (!excludedKeys.includes(key)) {
        acc[key] = rest[key];
      }
      return acc;
    }, {});

    return {
      name,
      email,
      mobile_number: cleanNumber,
      createdAt,
      other_data: { ...normalizedOtherData, ...restOtherData }
    };
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFilterInputs((prev) => ({ ...prev, [name]: value }))
    setActiveFilters(prev => ({ ...prev, [name]: value }));
  }

  const handleSelectChange = (e) => {
    const selectedFormId = e.target.value;
    if (!selectedFormId) {
      // All Forms selected → clear formId
      setFormId("");
      return;
    }
    const selectedItem = businessForm.find(item => item._id === selectedFormId);
    if (selectedItem) {
      handleClick(selectedItem);
    }
    setFormId(selectedFormId);
  };

  const handleApplyFilters = () => {
    const newParams = new URLSearchParams()
    Object.entries(filterInputs).forEach(([key, value]) => {
      if (value) newParams.set(key, value)
    })
    setSearchParams(newParams)
    setActiveFilters(filterInputs)
    // The useEffect with [formId, activeFilters.searchKeyword] will trigger
  }

  const handleClearFilters = () => {
    setFilterInputs(INITIAL_FILTERS)
    setActiveFilters(INITIAL_FILTERS)
    setSearchParams({})
    setPagination((prev) => ({ ...prev, page: 1 }))
    // The useEffect with [formId, activeFilters.searchKeyword] will trigger
  }

  const handlePageChange = (newPage) => {
    setPagination((prev) => ({ ...prev, page: newPage }))
    // The useEffect with [pagination.page, pagination.limit] will trigger
  }

  const handleLimitChange = (newLimit) => {
    const limit = parseInt(newLimit, 10);
    setPagination((prev) => ({
      ...prev,
      limit: limit,
      page: 1,
    }));
    // The useEffect with [pagination.page, pagination.limit] will trigger
  }

  const handleRefresh = () => {
    const urlFilters = getFiltersFromURL();
    setFilterInputs(urlFilters)
    setActiveFilters(urlFilters)
    setPagination({
      page: 1,
      limit: 10,
      totalCustomers: 0,
      totalPages: 1,
    })
    if (formId) {
      getregisterFormList(1, 10, urlFilters.searchKeyword);
    }
  }

  // functionality for other data like date range
  function formatCellValue(rawValue) {
    if (!rawValue) return "";

    // Handle objects (like date ranges)
    if (typeof rawValue === "object" && rawValue !== null) {
      if (rawValue.from && rawValue.to) {
        const format = (d) =>
          new Date(d).toISOString().split("T")[0]; // YYYY-MM-DD
        return `${format(rawValue.from)} - ${format(rawValue.to)}`;
      }
      return JSON.stringify(rawValue);
    }

    // If it's a string and looks like an ISO date
    if (typeof rawValue === "string" && /^\d{4}-\d{2}-\d{2}T/.test(rawValue)) {
      return new Date(rawValue).toISOString().split("T")[0]; // keep only YYYY-MM-DD
    }

    // Strip HTML if present
    return String(rawValue).replace(/<\/?[^>]+(>|$)/g, "");
  }

  const handleDataExport = () => {
    try {

          // ✅ Date formatter
         const formatDate = (dateString) => {
         if (!dateString) return "";
         const d = new Date(dateString);
         return d.toISOString().split("T")[0]; // yyyy-mm-dd
      };


      // ✅ Use the transformed data that you show in the table
      const tableData = transformedData.map(item => {
        const cleaned = {};
        Object.keys(item).forEach(key => {
          if (key !== "other_data") {

             let value = item[key];
           
                // Format createdAt field
               if (key === "createdAt") {
              value = formatDate(value);
                 }  

            // Clean HTML tags
            cleaned[key] =
              typeof value === "string"
                ? value.replace(/<\/?[^>]+(>|$)/g, "")
                : value;
          }
        });

        // Flatten other_data fields into columns
        if (item.other_data) {
          Object.entries(item.other_data).forEach(([k, v]) => {
            cleaned[k] = formatCellValue(v);
          });
        }

        return cleaned;
      });

      // Create worksheet & workbook
      const worksheet = XLSX.utils.json_to_sheet(tableData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Table Data");

      // Generate buffer
      const excelBuffer = XLSX.write(workbook, {
        bookType: "xlsx",
        type: "array",
      });

      // Trigger download
      const blob = new Blob([excelBuffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      saveAs(blob, `table_data_${Date.now()}.xlsx`);

      toast.success("Report downloaded successfully!");
    } catch (error) {
      console.error("Error exporting table data:", error);
      toast.error("Failed to export table data");
    }
  };

  const columns = React.useMemo(() => {
    if (!Array.isArray(transformedData) || transformedData.length === 0) return [];

    const baseKeys = ["name", "email", "mobile_number"];

     // Formatter for createdAt
    const formatDate = (dateString) => {
    if (!dateString) return "";
    const d = new Date(dateString);
    return d.toISOString().split("T")[0]; 
      };


    // Case 1: All Forms selected
    if (!formId) {
      const allFormColumns = baseKeys.map(key =>
        columnHelper.accessor(key, {
          header: key.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase()),
          cell: info => info.getValue() || ""
        })
      )
       // Add Created Date column
    allFormColumns.push(
      columnHelper.accessor("createdAt", {
        header: "Created Date",
        cell: info => formatDate(info.getValue()),
      })
    );

      allFormColumns.push(
        columnHelper.display({
          id: "action",
          header: "Action",
          cell: ({ row }) => (
            <Button
              size="sm"
              variant="outline"
              onClick={() => setSelectedRow(row.original)}
            >
              Details
            </Button>
          ),
        })
      )

      return allFormColumns
    }

    const dynamicColumns = baseKeys.map(key =>
      columnHelper.accessor(key, {
        header: key.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase()),
        cell: info => info.getValue() || ""
      })
    );


     // Add Created Date column
  dynamicColumns.push(
    columnHelper.accessor("createdAt", {
      header: "Created Date",
      cell: info => formatDate(info.getValue()),
    })
  );

    const otherDataKeys = [
      ...new Set(
        transformedData.flatMap(row =>
          row.other_data && typeof row.other_data === "object"
            ? Object.keys(row.other_data)
            : []
        )
      )
    ];

    otherDataKeys.forEach(key => {
      dynamicColumns.push(
        columnHelper.accessor(row => row.other_data?.[key], {
          id: `other_data.${key}`,
          header: key.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase()),
          cell: info => formatCellValue(info.getValue())
        })
      );
    });

    return dynamicColumns;
  }, [transformedData, formId]);

  return (
    <div className="p-6 bg-gray-50 min-h-screen space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
        <h1 className="text-2xl font-bold text-gray-900">Customers Demographics</h1>
        {/* <Button onClick={handleRefresh} variant="outline" disabled={isLoadingForms} className="gap-2">
          <ReloadIcon className={`h-4 w-4 ${isLoadingForms ? 'animate-spin' : ''}`} />
          Refresh
        </Button> */}
      </div>

      {/* Filters */}
      <div className="p-4 bg-white rounded-lg border space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-6 gap-4">
          <div className="relative xl:col-span-2">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              name="searchKeyword"
              placeholder="Search name, email, mobile..."
              value={filterInputs.searchKeyword}
              onChange={handleInputChange}
              onKeyDown={(e) => e.key === 'Enter' && handleApplyFilters()}
              className="w-full pl-9 pr-4 py-2 text-sm border rounded-md focus:ring-2 focus:ring-theme-color-800"
            />
          </div>
          <select
            name="formId"
            value={formId}
            onChange={handleSelectChange}
            className="text-sm border rounded-md py-2 px-3"
          >
            <option value="">All Forms</option>
            {businessForm?.map((item) => {
              return (
                <option key={item._id} value={item._id}>{item.name}</option>
              )
            })}
          </select>
          <input
            type="date"
            name="startDate"
            value={filterInputs.startDate}
            onChange={handleInputChange}
            className="text-sm border rounded-md py-2 px-3"
          />
          <input
            type="date"
            name="endDate"
            value={filterInputs.endDate}
            onChange={handleInputChange}
            className="text-sm border rounded-md py-2 px-3"
          />
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mt-2">
          <div className="flex flex-row flex-wrap gap-2">
            <Button onClick={handleApplyFilters} size="sm" className="bg-theme-color-800 hover:bg-theme-color-700 text-white">
              <MixerHorizontalIcon className="mr-1 h-4 w-4" />
              Apply
            </Button>
            <Button onClick={handleClearFilters} variant="ghost" size="sm" className="text-red-600">
              <Cross2Icon className="mr-1 h-4 w-4" />
              Clear
            </Button>
          </div>

          <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
            <Button
              className="bg-theme-color-800 hover:bg-theme-color-700 w-full sm:w-auto"
              onClick={handleDataExport}
              disabled={isExporting}
            >
              {isExporting ? (
                <>
                  <ReloadIcon className="mr-2 h-4 w-4 animate-spin" />
                  Exporting...
                </>
              ) : (
                <>
                  <DownloadIcon className="mr-2 h-4 w-4" />
                  Export data
                </>
              )}
            </Button>

            <div className="relative inline-block mr-3">
            <select
              id="itemsPerPage"
              name="itemsPerPage"
              value={pagination.limit}
              onChange={(e) => handleLimitChange(e.target.value)}
              // disabled={loading}
              className="block w-full sm:w-auto pl-3 pr-12 py-2 text-sm border-gray-300
               focus:outline-none focus:ring-theme-color-800 focus:border-theme-color-800
               rounded-md shadow-sm transition duration-150 ease-in-out"
                style={{ WebkitAppearance: 'none', MozAppearance: 'none', appearance: 'none' }}
              >
                <option value="10">10 per page</option>
                <option value="25">25 per page</option>
                <option value="50">50 per page</option>
                <option value="100">100 per page</option>
              </select>

              {/* custom arrow icon */}
              <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-800">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-4 w-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                </svg>
              </span>
            </div>
          </div>
        </div>
      </div>

      {isLoadingForms ? (
        <DataTableShimmer columns={5} />
      ) : !transformedData.length ? (
        <div className="text-center py-12 px-6 border border-dashed border-gray-300 rounded-lg bg-white">
          <h3 className="mt-3 text-lg font-semibold text-gray-800">No Customers Found</h3>
          <p className="mt-1 text-sm text-gray-500">
            {activeFilters.searchKeyword
              ? 'No customers match your search criteria.'
              : Object.values(activeFilters).some(Boolean)
                ? 'No customers match your filter criteria.'
                : "You don't have any customers yet."}
          </p>
        </div>
      ) : (
        <GenericTable
          columns={columns}
          data={transformedData}
          pagination={pagination}
          onPageChange={handlePageChange}
          onLimitChange={handleLimitChange}
          isLoading={isLoadingForms}
        />
      )}
      {selectedRow && (
        <div className="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg max-w-lg w-full p-6 space-y-4">
            <h2 className="text-lg font-semibold">Customer Details</h2>
            <div className="max-h-80 overflow-y-auto space-y-2">
              {Object.entries(selectedRow).map(([key, value]) => {
                if (key === "other_data" && typeof value === "object") {
                  return Object.entries(value).map(([subKey, subValue]) => (
                    <div key={subKey} className="flex justify-between border-b py-1">
                      <span className="font-medium">{subKey}</span>
                      <span className="text-gray-700">{formatCellValue(subValue)}</span>
                    </div>
                  ))
                }
                return (
                  <div key={key} className="flex justify-between border-b py-1">
                    <span className="font-medium">{key}</span>
                    <span className="text-gray-700">{formatCellValue(value)}</span>
                  </div>
                )
              })}
            </div>
            <div className="flex justify-end">
              <Button variant="outline" onClick={() => setSelectedRow(null)}>
                Close
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default BusinessReportsCustomerDemographics